/**
 * Ticket WebSocket Service
 * Source: /Users/omarsabbah/Desktop/Messaging-Hub/docs/developer/ticketing/frontend-ticketing/realtime-and-webhooks.md
 *
 * Handles all 12 real-time ticket events via Socket.IO
 * Connects to /messaging namespace
 * Events are broadcast to organization room: org:${printHouseId}
 */

import { Socket } from 'socket.io-client';
import { messagingService } from './messaging-socket.service';
import {
  TICKET_EVENTS,
  TicketCreatedPayload,
  TicketUpdatedPayload,
  TicketAssignedPayload,
  TicketStatusChangedPayload,
  TicketReplySentPayload,
  TicketReopenedPayload,
  TicketMergedPayload,
  TicketsBulkAssignedPayload,
  TicketMessageReceivedPayload,
  TicketCustomerUpdatedPayload,
  SLABreachedPayload,
  TicketAutoResponsePayload,
  WebhookMessageProcessedPayload,
  MessageDeliveredPayload,
  MessageDeliveryFailedPayload,
  ConversationNeedsReviewPayload,
  TicketTagsAddedPayload,
  TicketTagRemovedPayload,
  TagCreatedPayload,
  TagDeletedPayload,
  TicketAIStatusPayload,
  TicketAISuggestionPayload,
  TicketAIErrorPayload,
  EmailOrderSuggestionPayload,
  TicketCreatedHandler,
  TicketUpdatedHandler,
  TicketAssignedHandler,
  TicketStatusChangedHandler,
  TicketReplySentHandler,
  TicketReopenedHandler,
  TicketMergedHandler,
  TicketsBulkAssignedHandler,
  TicketMessageReceivedHandler,
  TicketCustomerUpdatedHandler,
  SLABreachedHandler,
  TicketAutoResponseHandler,
  WebhookMessageProcessedHandler,
  MessageDeliveredHandler,
  MessageDeliveryFailedHandler,
  ConversationNeedsReviewHandler,
  TicketTagsAddedHandler,
  TicketTagRemovedHandler,
  TagCreatedHandler,
  TagDeletedHandler,
  TicketAIStatusHandler,
  TicketAISuggestionHandler,
  TicketAIErrorHandler,
  EmailOrderSuggestionHandler,
} from '@/widgets/frontdesk/providers/tickets/types';

class TicketSocketService {
  private socket: Socket | null = null;
  private eventHandlers: Map<string, Set<(...args: any[]) => void>> = new Map();

  /**
   * Initialize socket connection and subscribe to ticket events
   * Backend extracts printHouseId from JWT token automatically
   */
  async connect(authToken: string): Promise<void> {
    // Use existing messaging service connection
    await messagingService.connect(authToken);
    this.socket = messagingService.getSocket();

    if (!this.socket) {
      throw new Error('Failed to connect to messaging service');
    }

    console.log('[TicketSocket] Connected to /messaging namespace (backend extracts printHouseId from JWT)');
    this.registerAllEvents();
  }

  /**
   * Disconnect from socket and clean up listeners
   */
  disconnect(): void {
    if (this.socket) {
      this.removeAllTicketListeners();
      this.socket = null;
      this.eventHandlers.clear();
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // ============================================
  // Event Registration (20 Ticket Events)
  // ============================================

  private registerAllEvents(): void {
    if (!this.socket) return;

    // Register all core ticket events
    this.socket.on(TICKET_EVENTS.CREATED, this.handleTicketCreated.bind(this));
    this.socket.on(TICKET_EVENTS.UPDATED, this.handleTicketUpdated.bind(this)); // New general update event
    this.socket.on(TICKET_EVENTS.ASSIGNED, this.handleTicketAssigned.bind(this));
    this.socket.on(TICKET_EVENTS.STATUS_CHANGED, this.handleTicketStatusChanged.bind(this));
    this.socket.on(TICKET_EVENTS.REPLY_SENT, this.handleTicketReplySent.bind(this));
    this.socket.on(TICKET_EVENTS.REOPENED, this.handleTicketReopened.bind(this));
    this.socket.on(TICKET_EVENTS.MERGED, this.handleTicketMerged.bind(this));
    this.socket.on(TICKET_EVENTS.BULK_ASSIGNED, this.handleTicketsBulkAssigned.bind(this));
    this.socket.on(TICKET_EVENTS.MESSAGE_RECEIVED, this.handleTicketMessageReceived.bind(this));
    this.socket.on(TICKET_EVENTS.CUSTOMER_UPDATED, this.handleTicketCustomerUpdated.bind(this));
    this.socket.on(TICKET_EVENTS.SLA_BREACHED, this.handleSLABreached.bind(this));
    this.socket.on(TICKET_EVENTS.AUTO_RESPONSE, this.handleTicketAutoResponse.bind(this));
    this.socket.on(TICKET_EVENTS.WEBHOOK_PROCESSED, this.handleWebhookMessageProcessed.bind(this));

    // Message delivery events
    this.socket.on(TICKET_EVENTS.MESSAGE_DELIVERED, this.handleMessageDelivered.bind(this));
    this.socket.on(TICKET_EVENTS.MESSAGE_DELIVERY_FAILED, this.handleMessageDeliveryFailed.bind(this));

    // Review queue event
    this.socket.on(TICKET_EVENTS.CONVERSATION_NEEDS_REVIEW, this.handleConversationNeedsReview.bind(this));

    // Tag events
    this.socket.on(TICKET_EVENTS.TAGS_ADDED, this.handleTicketTagsAdded.bind(this));
    this.socket.on(TICKET_EVENTS.TAG_REMOVED, this.handleTicketTagRemoved.bind(this));
    this.socket.on(TICKET_EVENTS.TAG_CREATED, this.handleTagCreated.bind(this));
    this.socket.on(TICKET_EVENTS.TAG_DELETED, this.handleTagDeleted.bind(this));

    // AI Suggestion events
    this.socket.on(TICKET_EVENTS.AI_STATUS, this.handleTicketAIStatus.bind(this));
    this.socket.on(TICKET_EVENTS.AI_SUGGESTION, this.handleTicketAISuggestion.bind(this));
    this.socket.on(TICKET_EVENTS.AI_ERROR, this.handleTicketAIError.bind(this));

    // Email Order Suggestion event
    this.socket.on(TICKET_EVENTS.EMAIL_ORDER_SUGGESTION, this.handleEmailOrderSuggestion.bind(this));

    console.log('[TicketSocket] Registered all 24 ticket event listeners (20 core + 3 AI + 1 email order)');
  }

  private removeAllTicketListeners(): void {
    if (!this.socket) return;

    Object.values(TICKET_EVENTS).forEach((eventName) => {
      this.socket!.removeAllListeners(eventName);
    });

    console.log('[TicketSocket] Removed all ticket event listeners');
  }

  // ============================================
  // Event Handlers (Internal)
  // ============================================
  // Note: Backend handles tenant isolation via Socket.IO rooms (org:${printHouseId})
  // No need to filter events on frontend - we only receive events for our organization

  private handleTicketCreated(payload: TicketCreatedPayload): void {
    console.log('[TicketSocket] ticket.created:', payload);
    this.emit(TICKET_EVENTS.CREATED, payload);
  }

  private handleTicketUpdated(payload: TicketUpdatedPayload): void {
    console.log('[TicketSocket] ticket.updated:', payload);
    console.log('[TicketSocket] Changes:', payload.changes);
    this.emit(TICKET_EVENTS.UPDATED, payload);
  }

  private handleTicketAssigned(payload: TicketAssignedPayload): void {
    console.log('[TicketSocket] ticket.assigned:', payload);
    this.emit(TICKET_EVENTS.ASSIGNED, payload);
  }

  private handleTicketStatusChanged(payload: TicketStatusChangedPayload): void {
    console.log('[TicketSocket] ticket.status.changed:', payload);
    this.emit(TICKET_EVENTS.STATUS_CHANGED, payload);
  }

  private handleTicketReplySent(payload: TicketReplySentPayload): void {
    console.log('[TicketSocket] ticket.reply.sent:', payload);
    this.emit(TICKET_EVENTS.REPLY_SENT, payload);
  }

  private handleTicketReopened(payload: TicketReopenedPayload): void {
    console.log('[TicketSocket] ticket.reopened:', payload);
    this.emit(TICKET_EVENTS.REOPENED, payload);
  }

  private handleTicketMerged(payload: TicketMergedPayload): void {
    console.log('[TicketSocket] ticket.merged:', payload);
    this.emit(TICKET_EVENTS.MERGED, payload);
  }

  private handleTicketsBulkAssigned(payload: TicketsBulkAssignedPayload): void {
    console.log('[TicketSocket] tickets.bulkAssigned:', payload);
    this.emit(TICKET_EVENTS.BULK_ASSIGNED, payload);
  }

  private handleTicketMessageReceived(payload: TicketMessageReceivedPayload): void {
    console.log('[TicketSocket] ticket.message.received:', payload);
    this.emit(TICKET_EVENTS.MESSAGE_RECEIVED, payload);
  }

  private handleTicketCustomerUpdated(payload: TicketCustomerUpdatedPayload): void {
    console.log('[TicketSocket] ticket.customer.updated:', payload);
    this.emit(TICKET_EVENTS.CUSTOMER_UPDATED, payload);
  }

  private handleSLABreached(payload: SLABreachedPayload): void {
    console.warn('[TicketSocket] sla.breached:', payload);
    this.emit(TICKET_EVENTS.SLA_BREACHED, payload);
  }

  private handleTicketAutoResponse(payload: TicketAutoResponsePayload): void {
    console.log('[TicketSocket] ticket.autoResponse:', payload);
    this.emit(TICKET_EVENTS.AUTO_RESPONSE, payload);
  }

  private handleWebhookMessageProcessed(payload: WebhookMessageProcessedPayload): void {
    console.log('[TicketSocket] webhook.message.processed:', payload);
    this.emit(TICKET_EVENTS.WEBHOOK_PROCESSED, payload);
  }

  // Message delivery handlers
  private handleMessageDelivered(payload: MessageDeliveredPayload): void {
    console.log('[TicketSocket] message.delivered:', payload);
    this.emit(TICKET_EVENTS.MESSAGE_DELIVERED, payload);
  }

  private handleMessageDeliveryFailed(payload: MessageDeliveryFailedPayload): void {
    console.error('[TicketSocket] message.delivery.failed:', payload);
    this.emit(TICKET_EVENTS.MESSAGE_DELIVERY_FAILED, payload);
  }

  // Review queue handler
  private handleConversationNeedsReview(payload: ConversationNeedsReviewPayload): void {
    console.log('[TicketSocket] conversation.needs.review:', payload);
    this.emit(TICKET_EVENTS.CONVERSATION_NEEDS_REVIEW, payload);
  }

  // Tag event handlers
  private handleTicketTagsAdded(payload: TicketTagsAddedPayload): void {
    console.log('[TicketSocket] ticket.tags.added:', payload);
    this.emit(TICKET_EVENTS.TAGS_ADDED, payload);
  }

  private handleTicketTagRemoved(payload: TicketTagRemovedPayload): void {
    console.log('[TicketSocket] ticket.tag.removed:', payload);
    this.emit(TICKET_EVENTS.TAG_REMOVED, payload);
  }

  private handleTagCreated(payload: TagCreatedPayload): void {
    console.log('[TicketSocket] tag.created:', payload);
    this.emit(TICKET_EVENTS.TAG_CREATED, payload);
  }

  private handleTagDeleted(payload: TagDeletedPayload): void {
    console.log('[TicketSocket] tag.deleted:', payload);
    this.emit(TICKET_EVENTS.TAG_DELETED, payload);
  }

  // AI Suggestion event handlers (conversation-based)
  private handleTicketAIStatus(payload: TicketAIStatusPayload): void {
    console.log('[TicketSocket] conversation-ai-status:', payload);
    this.emit(TICKET_EVENTS.AI_STATUS, payload);
  }

  private handleTicketAISuggestion(payload: TicketAISuggestionPayload): void {
    console.log('[TicketSocket] conversation-ai-suggestion:', payload);
    this.emit(TICKET_EVENTS.AI_SUGGESTION, payload);
  }

  private handleTicketAIError(payload: TicketAIErrorPayload): void {
    console.error('[TicketSocket] conversation-ai-error:', payload);
    this.emit(TICKET_EVENTS.AI_ERROR, payload);
  }

  private handleEmailOrderSuggestion(payload: EmailOrderSuggestionPayload): void {
    console.log('[TicketSocket] email-order-suggestion:', payload);
    this.emit(TICKET_EVENTS.EMAIL_ORDER_SUGGESTION, payload);
  }

  // ============================================
  // Public Event Listeners (for consumers)
  // ============================================

  /**
   * Subscribe to ticket.created event
   */
  onTicketCreated(handler: TicketCreatedHandler): void {
    this.on(TICKET_EVENTS.CREATED, handler);
  }

  /**
   * Subscribe to ticket.updated event
   * This is the primary event for tracking all ticket field updates
   */
  onTicketUpdated(handler: TicketUpdatedHandler): void {
    this.on(TICKET_EVENTS.UPDATED, handler);
  }

  /**
   * Subscribe to ticket.assigned event
   */
  onTicketAssigned(handler: TicketAssignedHandler): void {
    this.on(TICKET_EVENTS.ASSIGNED, handler);
  }

  /**
   * Subscribe to ticket.status.changed event
   */
  onTicketStatusChanged(handler: TicketStatusChangedHandler): void {
    this.on(TICKET_EVENTS.STATUS_CHANGED, handler);
  }

  /**
   * Subscribe to ticket.reply.sent event
   */
  onTicketReplySent(handler: TicketReplySentHandler): void {
    this.on(TICKET_EVENTS.REPLY_SENT, handler);
  }

  /**
   * Subscribe to ticket.reopened event
   */
  onTicketReopened(handler: TicketReopenedHandler): void {
    this.on(TICKET_EVENTS.REOPENED, handler);
  }

  /**
   * Subscribe to ticket.merged event
   */
  onTicketMerged(handler: TicketMergedHandler): void {
    this.on(TICKET_EVENTS.MERGED, handler);
  }

  /**
   * Subscribe to tickets.bulkAssigned event
   */
  onTicketsBulkAssigned(handler: TicketsBulkAssignedHandler): void {
    this.on(TICKET_EVENTS.BULK_ASSIGNED, handler);
  }

  /**
   * Subscribe to ticket.message.received event
   */
  onTicketMessageReceived(handler: TicketMessageReceivedHandler): void {
    this.on(TICKET_EVENTS.MESSAGE_RECEIVED, handler);
  }

  /**
   * Subscribe to ticket.customer.updated event
   */
  onTicketCustomerUpdated(handler: TicketCustomerUpdatedHandler): void {
    this.on(TICKET_EVENTS.CUSTOMER_UPDATED, handler);
  }

  /**
   * Subscribe to sla.breached event
   */
  onSLABreached(handler: SLABreachedHandler): void {
    this.on(TICKET_EVENTS.SLA_BREACHED, handler);
  }

  /**
   * Subscribe to ticket.autoResponse event
   */
  onTicketAutoResponse(handler: TicketAutoResponseHandler): void {
    this.on(TICKET_EVENTS.AUTO_RESPONSE, handler);
  }

  /**
   * Subscribe to webhook.message.processed event
   */
  onWebhookMessageProcessed(handler: WebhookMessageProcessedHandler): void {
    this.on(TICKET_EVENTS.WEBHOOK_PROCESSED, handler);
  }

  /**
   * Subscribe to message.delivered event
   */
  onMessageDelivered(handler: MessageDeliveredHandler): void {
    this.on(TICKET_EVENTS.MESSAGE_DELIVERED, handler);
  }

  /**
   * Subscribe to message.delivery.failed event
   */
  onMessageDeliveryFailed(handler: MessageDeliveryFailedHandler): void {
    this.on(TICKET_EVENTS.MESSAGE_DELIVERY_FAILED, handler);
  }

  /**
   * Subscribe to conversation.needs.review event
   * Fires when a message is received but no ticket exists
   */
  onConversationNeedsReview(handler: ConversationNeedsReviewHandler): void {
    this.on(TICKET_EVENTS.CONVERSATION_NEEDS_REVIEW, handler);
  }

  /**
   * Subscribe to ticket.tags.added event
   */
  onTicketTagsAdded(handler: TicketTagsAddedHandler): void {
    this.on(TICKET_EVENTS.TAGS_ADDED, handler);
  }

  /**
   * Subscribe to ticket.tag.removed event
   */
  onTicketTagRemoved(handler: TicketTagRemovedHandler): void {
    this.on(TICKET_EVENTS.TAG_REMOVED, handler);
  }

  /**
   * Subscribe to tag.created event
   */
  onTagCreated(handler: TagCreatedHandler): void {
    this.on(TICKET_EVENTS.TAG_CREATED, handler);
  }

  /**
   * Subscribe to tag.deleted event
   */
  onTagDeleted(handler: TagDeletedHandler): void {
    this.on(TICKET_EVENTS.TAG_DELETED, handler);
  }

  /**
   * Subscribe to conversation-ai-status event
   */
  onTicketAIStatus(handler: TicketAIStatusHandler): void {
    this.on(TICKET_EVENTS.AI_STATUS, handler);
  }

  /**
   * Subscribe to conversation-ai-suggestion event
   */
  onTicketAISuggestion(handler: TicketAISuggestionHandler): void {
    this.on(TICKET_EVENTS.AI_SUGGESTION, handler);
  }

  /**
   * Subscribe to conversation-ai-error event
   */
  onTicketAIError(handler: TicketAIErrorHandler): void {
    this.on(TICKET_EVENTS.AI_ERROR, handler);
  }

  /**
   * Subscribe to email-order-suggestion event
   */
  onEmailOrderSuggestion(handler: EmailOrderSuggestionHandler): void {
    this.on(TICKET_EVENTS.EMAIL_ORDER_SUGGESTION, handler);
  }

  // ============================================
  // Event Emitter Pattern (Internal)
  // ============================================

  private on(eventName: string, handler: (...args: any[]) => void): void {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Set());
    }
    this.eventHandlers.get(eventName)!.add(handler);
  }

  private emit(eventName: string, ...args: any[]): void {
    const handlers = this.eventHandlers.get(eventName);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`[TicketSocket] Error in ${eventName} handler:`, error);
        }
      });
    }
  }

  /**
   * Remove a specific event listener
   */
  removeListener(eventName: string, handler: (...args: any[]) => void): void {
    const handlers = this.eventHandlers.get(eventName);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Remove all listeners for a specific event
   */
  removeAllListeners(eventName?: string): void {
    if (eventName) {
      this.eventHandlers.delete(eventName);
    } else {
      this.eventHandlers.clear();
    }
  }
}

// Singleton instance
export const ticketSocketService = new TicketSocketService();
